import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DjayAvatar, DjayEmotion } from '../../mascot';
import { XPGainAnimation } from '../shared/XPGainAnimation';
import { getProgressToNextLevel, APP_NAME, PORTAL_LABELS } from '../../config/constants';
import { getLearnerNavItems } from './navigationHelpers';
import { dataLayer } from '../../data/dataLayer';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection, useVariantUnlocks, TOTAL_VARIANTS, useLongPressDiscovered } from '../../mascot';
import { StreakFlame } from '../shared/StreakFlame';
import { StreakMilestoneBurst, getCrossedMilestone } from '../shared/StreakMilestoneBurst';
import { LevelUpShockwave, LEVEL_UP_FILL_MS, LEVEL_UP_BURST_MS } from '../shared/LevelUpShockwave';
import { useStreakInDangerNudge } from '../../app/hooks/useStreakInDangerNudge';
import { soundManager } from '../../utils/soundManager';
import { ACHIEVEMENTS } from '../progress/AchievementsSection';
import { useAchievementCount } from '../shared/useAchievementCount';
import { getTimeContext } from '../../utils/timeContext';
import { useActiveAssignments } from '../../app/hooks/useActiveAssignments';
interface SidebarProps {
  currentView: string;
  onNavigate: (view: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}
export function Sidebar({
  currentView,
  onNavigate,
  isCollapsed,
  onToggleCollapse
}: SidebarProps) {
  // Pull required state from the global store
  const {
    state,
    currentLearnerDisplayName,
    viewingLearnerId,
    accounts
  } = useAppStore();
  // Derive values from store
  const stats = state.stats;
  const displayName = currentLearnerDisplayName;
  const activeAccountId = accounts?.activeAccountId || '';
  const activeAccount = accounts?.accountsById?.[activeAccountId];
  const activeAccountRole = viewingLearnerId ? 'learner' : activeAccount?.role || 'learner';
  const isLearner = activeAccountRole === 'learner';
  const isTeacherOrParent = activeAccountRole === 'teacher' || activeAccountRole === 'parent';
  // Avatar handling for learners
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (isLearner) {
      const loadAvatar = async () => {
        const prefs = await dataLayer.getLearnerPreferences();
        if (prefs?.avatarUrl) {
          setAvatarUrl(prefs.avatarUrl);
        }
      };
      loadAvatar();
    }
  }, [isLearner, accounts?.activeAccountId]);
  // Long-press discovery now derived from the easter-egg registry
  // (audit S4, ronde 3 2026).
  const hasDiscoveredLongPress = useLongPressDiscovered();
  const {
    selectedVariant
  } = useDjaySelection();
  // Use shared variant unlock hook
  const {
    unlockedCount
  } = useVariantUnlocks(hasDiscoveredLongPress);
  // Use shared achievement count hook (single source of truth)
  const unlockedAchievementsCount = useAchievementCount();
  // Active assignments (audit P1-3) — single source of truth across
  // HomePage, Sidebar and MobileNav. We only need the `hasAny` flag here
  // for the oranje dot on the Toetsen nav-item.
  const {
    hasAny: hasActiveAssignments
  } = useActiveAssignments(isLearner ? activeAccountId : null);
  // Calculate XP progress for the profile section
  const progressToNextLevel = stats?.xp != null ? getProgressToNextLevel(stats.xp).percent : 0;
  // Streak from account state
  const streak = activeAccount?.appState?.streak?.currentTotalStreak || 0;
  // ── Streak milestone burst (Levende Sidebar — App-Feel §3.4) ──
  // When the streak crosses a celebrated threshold (3/7/14/30/60/100),
  // remount StreakMilestoneBurst once via an incrementing key. Detection
  // mirrors the XP-gain pattern — local useRef, no global state. Reloading
  // at-or-above the milestone does NOT refire (initial ref == current).
  const prevStreakRef = useRef<number>(streak);
  const [streakBurst, setStreakBurst] = useState<{
    milestone: number;
    key: number;
  } | null>(null);
  useEffect(() => {
    const prev = prevStreakRef.current;
    const crossed = getCrossedMilestone(prev, streak);
    if (crossed !== null) {
      setStreakBurst(b => ({
        milestone: crossed,
        key: (b?.key ?? 0) + 1
      }));
    }
    prevStreakRef.current = streak;
  }, [streak]);
  // ── Streak-in-danger nudge (App-Feel §16.C) ──
  // Returns true when streak ≥ 3, no activity today, and hour ≥ 17.
  // Cosmetic only — adds a slow pulse-ring on the StreakFlame.
  const {
    inDanger: streakInDanger
  } = useStreakInDangerNudge();
  // ── XP-gain detection (Levende Sidebar — App-Feel §3.4) ──
  // Watch XP changes and trigger a one-shot floating "+X XP" + glow pulse
  // on the sidebar XP bar whenever XP increases. Resets after the animation.
  const currentXp = stats?.xp ?? 0;
  const prevXpRef = useRef<number>(currentXp);
  const [xpGain, setXpGain] = useState<number>(0);
  const [xpPulseKey, setXpPulseKey] = useState<number>(0);
  useEffect(() => {
    const prev = prevXpRef.current;
    if (currentXp > prev) {
      const delta = currentXp - prev;
      setXpGain(delta);
      setXpPulseKey(k => k + 1);
    }
    prevXpRef.current = currentXp;
  }, [currentXp]);
  // ── Level-up shockwave (App-Feel §16.B) ──
  // When the learner crosses a level boundary, choreograph the sidebar
  // XP-bar through three phases:
  //   1. fill  → bar animates to 100% (acknowledges "you completed the prev level")
  //   2. burst → LevelUpShockwave plays on top of the full bar (radial rays + "Level X!")
  //   3. idle  → bar snaps back to the new level's actual progress %
  // We track the level via prevLevelRef and a phased state machine so we can
  // override the bar's width during phases 1+2. After idle returns, the bar's
  // motion.div picks up the real `progressToNextLevel` again.
  const currentLevel = stats?.level ?? 1;
  const prevLevelRef = useRef<number>(currentLevel);
  const [levelUpPhase, setLevelUpPhase] = useState<'idle' | 'filling' | 'bursting'>('idle');
  const [levelUpKey, setLevelUpKey] = useState<number>(0);
  const [levelUpFor, setLevelUpFor] = useState<number>(currentLevel);
  useEffect(() => {
    const prev = prevLevelRef.current;
    if (currentLevel > prev) {
      setLevelUpFor(currentLevel);
      setLevelUpKey(k => k + 1);
      setLevelUpPhase('filling');
      const t1 = setTimeout(() => setLevelUpPhase('bursting'), LEVEL_UP_FILL_MS);
      const t2 = setTimeout(() => setLevelUpPhase('idle'), LEVEL_UP_FILL_MS + LEVEL_UP_BURST_MS);
      prevLevelRef.current = currentLevel;
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    prevLevelRef.current = currentLevel;
  }, [currentLevel]);
  // Effective XP-bar fill: 100% while choreographing the level-up, otherwise
  // the real progress to next level. The bar's motion.div animates the width
  // between values, so the transitions feel intentional rather than jarring.
  const effectiveBarFill = levelUpPhase === 'idle' ? progressToNextLevel : 100;
  // ── View-aware Djay emotion (Levende Sidebar — App-Feel §3.4) ──
  // For a short window after the learner navigates to a meaningful page,
  // Djay reflects that context. After the window expires we fall back to
  // the time/streak baseline so Djay still "lives" between events.
  const [viewEmotionUntil, setViewEmotionUntil] = useState<number>(0);
  const viewEmotionRef = useRef<DjayEmotion | null>(null);
  useEffect(() => {
    if (!isLearner) return;
    const viewToEmotion: Record<string, DjayEmotion> = {
      collection: 'celebrating',
      progress: 'proud',
      subjects: 'thinking',
      assessment: 'encouraging'
    };
    const next = viewToEmotion[currentView];
    if (next) {
      viewEmotionRef.current = next;
      setViewEmotionUntil(Date.now() + 3500);
    }
  }, [currentView, isLearner]);
  // Lightweight tick so the emotion can revert without a global timer
  const [, force] = useState(0);
  useEffect(() => {
    if (viewEmotionUntil === 0) return;
    const t = setTimeout(() => force(n => n + 1), viewEmotionUntil - Date.now());
    return () => clearTimeout(t);
  }, [viewEmotionUntil]);
  // Compose the final emotion: view-specific (transient) → streak/time baseline
  let contextEmotion: DjayEmotion = 'happy';
  if (viewEmotionRef.current && Date.now() < viewEmotionUntil) {
    contextEmotion = viewEmotionRef.current;
  } else {
    // Shared time-of-day window (audit §P2-6) — replaces the inline
    // `hour < 8 || hour > 20` check so Sidebar, MobileNav and FloatingDjay
    // can never drift apart again.
    const {
      djaySleepy
    } = getTimeContext();
    if (djaySleepy) contextEmotion = 'sleeping';else if (streak >= 5) contextEmotion = 'excited';else if (streak === 0) contextEmotion = 'encouraging';
  }
  return <motion.aside className="hidden md:flex flex-col h-full bg-white border-r-2 border-slate-100 sticky top-0 z-30 shadow-[4px_0_24px_rgba(249,115,22,0.03)]" initial={false} animate={{
    width: isCollapsed ? 88 : 280
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 30
  }} data-id="element-1471">
      {/* Logo Section - Playful */}
      <div className="h-24 flex items-center px-7 relative overflow-hidden" data-id="element-1472">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-orange-50/50 to-transparent pointer-events-none" data-id="element-1473"></div>

        {/* @djay-slot: sidebar-logo */}
        <motion.div className="flex-shrink-0 relative z-10" whileHover={{
        rotate: 10,
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} data-id="element-1474">
          <DjayAvatar size="lg" emotion={contextEmotion} variant={selectedVariant} data-id="element-1475" />
        </motion.div>

        <AnimatePresence data-id="element-1476">
          {!isCollapsed && <motion.span initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -10
        }} className="font-black text-slate-800 ml-4 text-2xl tracking-tight whitespace-nowrap overflow-hidden relative z-10" data-id="element-1477">
              {APP_NAME}
            </motion.span>}
        </AnimatePresence>
      </div>

      {/* Navigation Items - Chunky & Colorful */}
      <nav className="flex-1 px-4 space-y-2 mt-4" data-id="element-1478">
        {getLearnerNavItems().map(item => {
        const isActive = currentView === item.id;
        const Icon = item.icon;
        return <motion.button key={item.id} onClick={() => {
          soundManager.playNavigate();
          onNavigate(item.id);
        }} className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-xl
                transition-colors group relative
                ${isActive ? 'text-orange-700' : 'text-slate-600 hover:bg-slate-100/80'}
              `} whileHover={{
          x: isCollapsed ? 0 : 4
        }} whileTap={{
          scale: 0.98
        }} data-id="element-1479">
              {isActive && <motion.div layoutId="activeNavIndicator" className="absolute inset-0 bg-orange-100 rounded-xl border border-orange-200 shadow-sm" initial={false} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} data-id="element-1480" />}
              <div className="relative flex-shrink-0 z-10" data-id="element-1481">
                <Icon className={`w-6 h-6 transition-transform duration-300 ${isActive ? 'animate-wiggle text-orange-600' : 'group-hover:scale-110'}`} strokeWidth={isActive ? 2.5 : 2} data-id="element-1482" />
                {item.id === 'assessment' && hasActiveAssignments && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-500 border-2 border-white rounded-full" data-id="element-1483" />}
              </div>
              <AnimatePresence data-id="element-1484">
                {!isCollapsed && <motion.span initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} className={`ml-4 whitespace-nowrap overflow-hidden relative z-10 text-base ${isActive ? 'font-black' : 'font-bold'}`} data-id="element-1485">
                    {item.label}
                  </motion.span>}
              </AnimatePresence>
            </motion.button>;
      })}
      </nav>

      {/* Mijn Collectie Button */}
      {isLearner && <div className="px-4 mt-2 mb-2" data-id="element-1486">
          <motion.button onClick={() => {
        soundManager.playNavigate();
        onNavigate('collection');
      }} className={`
              w-full flex items-center gap-3 p-2 rounded-2xl border-2 transition-all group relative overflow-hidden
              ${currentView === 'collection' ? 'border-orange-400 bg-orange-50 ring-2 ring-orange-400 ring-offset-1' : 'border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 hover:border-orange-300 hover:shadow-md'}
            `} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} data-id="element-1487">
            <div className="relative flex-shrink-0" data-id="element-1488">
              <motion.div animate={{
            y: [0, -3, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-1489">
                <DjayAvatar size="sm" emotion="excited" variant={selectedVariant} data-id="element-1490" />
              </motion.div>
            </div>

            <AnimatePresence data-id="element-1491">
              {!isCollapsed && <motion.div initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -10
          }} className="flex-1 text-left overflow-hidden" data-id="element-1492">
                  <span className="block font-black text-orange-900 text-sm truncate" data-id="element-1493">
                    Mijn Collectie
                  </span>
                  <span className="block text-[9px] font-bold text-orange-600 truncate" data-id="element-1494">
                    {unlockedCount}/{TOTAL_VARIANTS} Varianten •{' '}
                    {unlockedAchievementsCount}/{ACHIEVEMENTS.length}{' '}
                    Achievements
                  </span>
                </motion.div>}
            </AnimatePresence>
          </motion.button>
        </div>}

      {/* Learner Profile Section */}
      {isLearner && !isCollapsed && stats && <div className="mt-auto pt-4 border-t border-gray-200" data-id="element-1495">
          <div className="px-4 py-3" data-id="element-1496">
            {/* Avatar and Name */}
            <div className="flex items-center gap-3 mb-3" data-id="element-1497">
              {/* Avatar with custom image support */}
              {avatarUrl ? <img src={avatarUrl} alt={displayName} className="w-12 h-12 rounded-full object-cover flex-shrink-0" data-id="element-1498" /> : <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-1499">
                  {displayName.charAt(0).toUpperCase()}
                </div>}
              <div className="flex-1 min-w-0" data-id="element-1500">
                <p className="font-semibold text-gray-900 truncate" data-id="element-1501">
                  {displayName}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1502">Level {stats.level}</p>
              </div>

              {/* Streak Flame + milestone burst overlay + in-danger nudge (§16.C) */}
              <div className="flex-shrink-0 relative" data-id="element-1503">
                <StreakFlame streak={streak} size="sm" inDanger={streakInDanger} data-id="element-1504" />
                {streakBurst && <StreakMilestoneBurst key={streakBurst.key} milestone={streakBurst.milestone} onComplete={() => setStreakBurst(null)} data-id="element-1505" />}
              </div>
            </div>

            {/* XP Progress */}
            <div className="space-y-1 relative" data-id="element-1506">
              <div className="flex items-center justify-between text-xs text-gray-600" data-id="element-1507">
                <span data-id="element-1508">XP</span>
                <span data-id="element-1509">
                  {stats.xp} / {getProgressToNextLevel(stats.xp).nextLevelXP}
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden relative" data-id="element-1510">
                <motion.div className="h-full bg-gradient-to-r from-purple-500 to-blue-500" initial={{
              width: 0
            }} animate={{
              width: `${effectiveBarFill}%`
            }} transition={levelUpPhase === 'filling' ? {
              duration: 0.45,
              ease: 'easeOut'
            } : {
              type: 'spring',
              stiffness: 150,
              damping: 12
            }} data-id="element-1511" />
                {/* Shine pulse — fires once per XP gain */}
                <AnimatePresence data-id="element-1512">
                  {xpPulseKey > 0 && <motion.div key={xpPulseKey} className="absolute inset-y-0 w-8 pointer-events-none" style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.85), transparent)',
                mixBlendMode: 'screen'
              }} initial={{
                x: '-100%',
                opacity: 0
              }} animate={{
                x: '420%',
                opacity: [0, 1, 0]
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.9,
                ease: 'easeOut'
              }} data-id="element-1513" />}
                </AnimatePresence>
              </div>
              {/* Floating "+X XP" — fires once per XP gain */}
              {xpGain > 0 && <XPGainAnimation key={xpPulseKey} amount={xpGain} className="left-1/2 -translate-x-1/2 -top-2" onComplete={() => setXpGain(0)} data-id="element-1514" />}
              {/* Level-up shockwave (§16.B) — fires once per level boundary.
               Sits on a sibling layer so the rays/glow can paint past the
               XP-bar's overflow-hidden clip. Only mounted during the
               fill+burst phases so it does not interfere with idle state. */}
              {levelUpPhase !== 'idle' && <LevelUpShockwave key={levelUpKey} level={levelUpFor} data-id="element-1515" />}
            </div>
          </div>
        </div>}

      {/* Teacher/Parent Profile Section */}
      {isTeacherOrParent && !isCollapsed && <div className="mt-auto pt-4 border-t border-gray-200" data-id="element-1516">
          <div className="px-4 py-3" data-id="element-1517">
            <div className="flex items-center gap-3 mb-3" data-id="element-1518">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-1519">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0" data-id="element-1520">
                <p className="font-semibold text-gray-900 truncate" data-id="element-1521">
                  {displayName}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1522">
                  {PORTAL_LABELS[activeAccountRole] ?? activeAccountRole}
                </p>
              </div>
            </div>
          </div>
        </div>}

      {/* Collapse Toggle */}
      <button onClick={onToggleCollapse} className="absolute -right-3.5 top-28 w-7 h-7 bg-white border-2 border-slate-200 rounded-full flex items-center justify-center shadow-sm text-slate-400 hover:text-orange-500 hover:border-orange-300 hover:scale-110 transition-all z-40 focus:outline-none" aria-label={isCollapsed ? 'Uitklappen' : 'Inklappen'} data-id="element-1523">
        {isCollapsed ? <ChevronRight className="w-4 h-4 stroke-[3]" data-id="element-1524" /> : <ChevronLeft className="w-4 h-4 stroke-[3]" data-id="element-1525" />}
      </button>
    </motion.aside>;
}